const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAY_ORDER_INDEX = new Map(WEEKDAY_ORDER.map((day, index) => [day, index]));

const isValidWeekday = (day) => Number.isInteger(day) && day >= 0 && day <= 6;

export const formatScheduleMinutesTime = (minutes) => {
  const bounded = Math.max(0, Math.min(1439, Number(minutes) || 0));
  return `${String(Math.floor(bounded / 60)).padStart(2, "0")}:${String(
    bounded % 60
  ).padStart(2, "0")}`;
};

const getWeekdayLabel = (day, language = "en") => {
  const orderIndex = WEEKDAY_ORDER_INDEX.get(day) ?? 0;
  const monday = Date.UTC(2023, 0, 2);
  const date = new Date(monday + orderIndex * 86400000);
  return new Intl.DateTimeFormat(language === "de" ? "de-AT" : "en-US", {
    weekday: "short",
  })
    .format(date)
    .replace(/\.$/, "");
};

const normalizeWeekdays = (weekdays) =>
  [...new Set((Array.isArray(weekdays) ? weekdays : []).map(Number).filter(isValidWeekday))].sort(
    (left, right) => WEEKDAY_ORDER_INDEX.get(left) - WEEKDAY_ORDER_INDEX.get(right)
  );

const splitWeekdaySegments = (weekdays) =>
  normalizeWeekdays(weekdays).reduce((segments, day) => {
    const previousSegment = segments[segments.length - 1];
    const previousDay = previousSegment?.[previousSegment.length - 1];
    const isConsecutive =
      previousDay !== undefined &&
      WEEKDAY_ORDER_INDEX.get(day) === WEEKDAY_ORDER_INDEX.get(previousDay) + 1;

    if (isConsecutive) {
      previousSegment.push(day);
      return segments;
    }

    segments.push([day]);
    return segments;
  }, []);

export const formatWeekdayGroup = (weekdays, language = "en") =>
  splitWeekdaySegments(weekdays)
    .map((segment) => {
      if (segment.length === 1) return getWeekdayLabel(segment[0], language);
      return `${getWeekdayLabel(segment[0], language)}-${getWeekdayLabel(
        segment[segment.length - 1],
        language
      )}`;
    })
    .join(", ");

const readMinutes = (window, names, fallback = 0) => {
  const value = names.map((name) => window?.[name]).find((candidate) => candidate !== undefined);
  const minutes = Number.parseInt(value, 10);
  return Number.isFinite(minutes) ? Math.max(0, Math.min(1439, minutes)) : fallback;
};

const readWindowWeekdays = (window) => {
  if (Array.isArray(window?.weekdays)) return window.weekdays;
  if (window?.weekday !== undefined) return [window.weekday];
  if (window?.dayOfWeek !== undefined) return [window.dayOfWeek];
  if (window?.day !== undefined) return [window.day];
  return [];
};

const normalizeScheduleWindows = (schedule) => {
  const configuredWindows = Array.isArray(schedule?.windows)
    ? schedule.windows
    : Array.isArray(schedule?.dayWindows)
      ? schedule.dayWindows
      : null;

  const rawWindows =
    configuredWindows ||
    (Array.isArray(schedule?.weekdays)
      ? [
          {
            weekdays: schedule.weekdays,
            pickupStartMinutes: schedule.pickupStartMinutes,
            pickupEndMinutes: schedule.pickupEndMinutes,
            returnStartMinutes: schedule.returnStartMinutes,
            returnEndMinutes: schedule.returnEndMinutes,
          },
        ]
      : []);

  const grouped = new Map();
  rawWindows.forEach((window) => {
    const startMinutes = readMinutes(
      window,
      ["startMinutes", "pickupStartMinutes", "returnStartMinutes"],
      0
    );
    const endMinutes = readMinutes(
      window,
      ["endMinutes", "returnEndMinutes", "pickupEndMinutes"],
      startMinutes
    );
    const weekdays = normalizeWeekdays(readWindowWeekdays(window));
    if (!weekdays.length) return;

    const key = `${startMinutes}:${endMinutes}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.weekdays.push(...weekdays);
      return;
    }

    grouped.set(key, { weekdays: [...weekdays], startMinutes, endMinutes });
  });

  return [...grouped.values()];
};

export const buildSelfAdjustingScheduleLabel = (schedule, language = "en") =>
  normalizeScheduleWindows(schedule)
    .map((window) => {
      const weekdayLabel = formatWeekdayGroup(window.weekdays, language);
      if (!weekdayLabel) return "";
      return `${weekdayLabel} · ${formatScheduleMinutesTime(
        window.startMinutes
      )}-${formatScheduleMinutesTime(window.endMinutes)}`;
    })
    .filter(Boolean)
    .join(", ");
